import { useRef, useCallback, useEffect } from "react";

/**
 * useLongPress — press-and-hold OR right-click detection.
 *
 * Returns a bag of handlers to spread onto any interactive element.
 * Long-press threshold is 450ms by default; right-click fires
 * immediately. A `suppressClickRef` stops the click that follows a
 * long-press from also firing the element's `onClick`.
 *
 * Usage:
 *   const { onClick, onContextMenu, ...longPressProps } = useLongPress({
 *     onLongPress: (e) => setPeek(...),
 *     onClick: () => openCheckin(),
 *     disabled: locked,
 *   });
 *   <button {...longPressProps} onClick={onClick} onContextMenu={onContextMenu} />
 */
export function useLongPress({ onLongPress, onClick, disabled, ms = 450 } = {}) {
  const timerRef = useRef(null);
  const suppressRef = useRef(false);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  const fire = useCallback(
    (e) => {
      if (disabled || !onLongPress) return;
      suppressRef.current = true;
      onLongPress(e);
      try {
        if (navigator.vibrate) navigator.vibrate(15);
      } catch {}
    },
    [disabled, onLongPress]
  );

  const start = useCallback(
    (e) => {
      if (disabled || !onLongPress) return;
      clearTimeout(timerRef.current);
      // Capture the event shape synchronously — React pools events.
      const target = e.currentTarget;
      timerRef.current = setTimeout(() => fire({ currentTarget: target }), ms);
    },
    [disabled, onLongPress, fire, ms]
  );

  const cancel = useCallback(() => {
    clearTimeout(timerRef.current);
    timerRef.current = null;
  }, []);

  const handleClick = useCallback(
    (e) => {
      if (suppressRef.current) {
        suppressRef.current = false;
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      onClick && onClick(e);
    },
    [onClick]
  );

  const handleContextMenu = useCallback(
    (e) => {
      if (disabled || !onLongPress) return;
      e.preventDefault();
      fire(e);
    },
    [disabled, onLongPress, fire]
  );

  const handleMouseDown = useCallback(
    (e) => {
      if (e.button !== 0) return;
      start(e);
    },
    [start]
  );

  return {
    onClick: handleClick,
    onContextMenu: handleContextMenu,
    onMouseDown: handleMouseDown,
    onMouseUp: cancel,
    onMouseLeave: cancel,
    onTouchStart: start,
    onTouchMove: cancel,
    onTouchEnd: cancel,
    onTouchCancel: cancel,
  };
}
